import React, { ReactElement, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Link } from 'react-router-dom';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import ChangeRequestFilterableList from './ChangeRequestFilterableList';
import ProjectChangeRequestsIncomplete from './ProjectChangeRequestsIncomplete';
import ChangeRequestOverview from './ChangeRequestOverview';
import ChangeRequestMessagesList from './ChangeRequestMessagesList';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { CommentThreadType } from '@/types';
import { useUnreadThreadCount } from '@/utils/hooks';
import { Tabs } from '@ygtechnologies/rubus-ui';

// export type ChangeRequestsPageTab = 'all' | 'overview' | 'unassigned' | 'awaiting-offers' | 'ready-to-send' | 'rejected';

export enum ChangeRequestsPageTab {
    All = 'all',
    Overview = 'overview',
    Tasks = 'tasks',
    Messages = 'messages',
}

interface TabOptions {
    projectId: number;
}

interface TabSetup {
    id: ChangeRequestsPageTab;
    name: ReactElement;
    url: string;
    render(options: TabOptions): ReactElement;
    notificationCount?: number | null;
    visible: boolean;
}
interface ChangeRequestsPageProps {
    projectId: string | number;
    tab: ChangeRequestsPageTab | undefined;
}

const ChangeRequestsPage: React.FC<ChangeRequestsPageProps> = ({ projectId, tab }) => {
    const projectOverview = useSelector((state: IState) => state.seller.projectOverview);
    const unreadMessages = useUnreadThreadCount();
    const project = useSelector((state: IState) => state.global.activeProject);

    const tabs: TabSetup[] = useMemo(
        () => [
            {
                id: ChangeRequestsPageTab.All,
                url: 'changes',
                name: <Trans>All change requests</Trans>,
                render: (options) => <ChangeRequestFilterableList mode="all" {...options} />,
                visible: true,
            },
            {
                id: ChangeRequestsPageTab.Overview,
                url: 'changes/overview',
                name: <Trans>Overview</Trans>,
                render: (options) => <ChangeRequestOverview {...options} />, //<ChangeRequestFilterableList mode="tasks" {...options} />,
                visible:
                    !!project && (project.permissions.canAssignChangeRequests || project.permissions.canSendChangeRequests),
            },
            {
                id: ChangeRequestsPageTab.Tasks,
                url: 'changes/tasks',
                name: <Trans>My tasks</Trans>,
                render: (options) => <ProjectChangeRequestsIncomplete {...options} />,
                notificationCount: projectOverview.changeRequests.total,
                visible: true,
            },
            {
                id: ChangeRequestsPageTab.Messages,
                url: 'changes/messages',
                name: <Trans>By last message</Trans>,
                render: (options) => <ChangeRequestMessagesList {...options} />,
                notificationCount: unreadMessages[CommentThreadType.ChangeRequest],
                visible: true,
            },
        ],
        [projectOverview, unreadMessages, project]
    );

    const visibleTabs = tabs.filter((x) => x.visible);
    const activeTabId = tab ?? ChangeRequestsPageTab.All;
    const activeTab = visibleTabs.find((x) => x.id === activeTabId);

    return (
        <>
            <SimplePageTitle title={t`Change requests`} />

            <Tabs.Container activeId={activeTabId} variant="underline" sticky stickyClassName="stickyBleed">
                {visibleTabs.map((t) => (
                    <Tabs.Tab
                        key={t.id}
                        id={t.id}
                        as={Link}
                        to={`/projects/${projectId}/${t.url}`}
                        label={t.name}
                        badge={t.notificationCount ?? undefined}
                    />
                ))}
                <Tabs.Content>{activeTab?.render({ projectId: +projectId })}</Tabs.Content>
            </Tabs.Container>
        </>
    );
};

export default ChangeRequestsPage;
